/**
 * Authentication store — Keycloak OIDC via PKCE flow.
 * The manager portal authenticates against the "careerforge-employee" realm
 * using the "manager-portal" client. After token exchange the store verifies
 * that the user holds the "manager" role in realm_access.roles.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { RUNTIME_CONFIG } from '@/runtime-config'

const KEYCLOAK_URL = RUNTIME_CONFIG.KEYCLOAK_URL
const REALM = RUNTIME_CONFIG.KEYCLOAK_REALM
const CLIENT_ID = RUNTIME_CONFIG.KEYCLOAK_CLIENT_ID
const REDIRECT_URI = window.location.origin + '/login'
const STORAGE_KEY = 'cf_manager_tokens'

interface TokenSet {
  accessToken: string
  refreshToken: string
  expiresAt: number // Unix timestamp ms
}

export const useAuthStore = defineStore('auth', () => {
  const tokenSet = ref<TokenSet | null>(_load())
  const userInfo = ref<Record<string, unknown> | null>(null)
  const hasManagerRole = ref<boolean>(false)

  const isAuthenticated = computed(() => !!tokenSet.value && tokenSet.value.expiresAt > Date.now())
  const accessToken = computed(() => tokenSet.value?.accessToken ?? null)

  async function initSession() {
    if (!tokenSet.value) return
    if (tokenSet.value.expiresAt < Date.now() + 60_000) await _refresh()
    if (tokenSet.value && !userInfo.value) await _fetchUserInfo()
  }

  async function getAccessToken(): Promise<string | null> {
    if (!tokenSet.value) return null
    if (tokenSet.value.expiresAt < Date.now() + 60_000) await _refresh()
    return tokenSet.value?.accessToken ?? null
  }

  async function _refresh() {
    if (!tokenSet.value?.refreshToken) { logout(); return }
    try {
      const params = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: CLIENT_ID,
        refresh_token: tokenSet.value.refreshToken,
      })
      const res = await fetch(
        `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/token`,
        { method: 'POST', body: params },
      )
      if (!res.ok) throw new Error('refresh failed')
      _set(await res.json())
    } catch { logout() }
  }

  async function _fetchUserInfo() {
    const token = await getAccessToken()
    if (!token) return
    const res = await fetch(
      `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/userinfo`,
      { headers: { Authorization: `Bearer ${token}` } },
    )
    if (res.ok) {
      userInfo.value = await res.json()
    }
    // Inspect the JWT payload for realm_access.roles
    _checkManagerRole()
  }

  function _checkManagerRole() {
    if (!tokenSet.value?.accessToken) { hasManagerRole.value = false; return }
    try {
      const payload = JSON.parse(atob(tokenSet.value.accessToken.split('.')[1]))
      const roles: string[] = (payload?.realm_access?.roles as string[]) ?? []
      hasManagerRole.value = roles.includes('manager')
    } catch {
      hasManagerRole.value = false
    }
  }

  async function redirectToLogin() {
    const state = crypto.randomUUID()
    const verifier = _pkceVerifier()
    const challenge = await _pkceChallenge(verifier)
    sessionStorage.setItem('oauth_state', state)
    sessionStorage.setItem('pkce_verifier', verifier)
    const url = new URL(`${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/auth`)
    url.searchParams.set('response_type', 'code')
    url.searchParams.set('client_id', CLIENT_ID)
    url.searchParams.set('redirect_uri', REDIRECT_URI)
    url.searchParams.set('scope', 'openid profile email')
    url.searchParams.set('state', state)
    url.searchParams.set('code_challenge', challenge)
    url.searchParams.set('code_challenge_method', 'S256')
    window.location.href = url.toString()
  }

  async function handleCallback(code: string, state: string) {
    if (state !== sessionStorage.getItem('oauth_state')) throw new Error('Invalid OAuth state')
    const verifier = sessionStorage.getItem('pkce_verifier')
    if (!verifier) throw new Error('Missing PKCE verifier')
    sessionStorage.removeItem('oauth_state')
    sessionStorage.removeItem('pkce_verifier')
    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      code,
      code_verifier: verifier,
    })
    const res = await fetch(
      `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/token`,
      { method: 'POST', body: params },
    )
    if (!res.ok) throw new Error('Token exchange failed')
    _set(await res.json())
    await _fetchUserInfo()
  }

  function _pkceVerifier(): string {
    const arr = new Uint8Array(32)
    crypto.getRandomValues(arr)
    return btoa(String.fromCharCode(...arr)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
  }

  async function _pkceChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder()
    const data = encoder.encode(verifier)
    const digest = await crypto.subtle.digest('SHA-256', data)
    return btoa(String.fromCharCode(...new Uint8Array(digest))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
  }

  function _set(data: { access_token: string; refresh_token: string; expires_in: number }) {
    const ts: TokenSet = {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresAt: Date.now() + data.expires_in * 1000,
    }
    tokenSet.value = ts
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ts))
    _checkManagerRole()
  }

  function logout() {
    tokenSet.value = null
    userInfo.value = null
    hasManagerRole.value = false
    localStorage.removeItem(STORAGE_KEY)
    const url = new URL(`${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/logout`)
    url.searchParams.set('client_id', CLIENT_ID)
    url.searchParams.set('post_logout_redirect_uri', window.location.origin + '/login')
    window.location.href = url.toString()
  }

  // Check role on initial load from storage
  if (tokenSet.value) _checkManagerRole()

  return {
    isAuthenticated,
    accessToken,
    userInfo,
    hasManagerRole,
    initSession,
    getAccessToken,
    redirectToLogin,
    handleCallback,
    logout,
  }
})

function _load(): TokenSet | null {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') } catch { return null }
}
